import React, {useCallback, useRef} from 'react'
import {useRouter as useNextRouter} from 'next/router'

import {parseQueryString} from '../../../utils/url'
import {RouterContext, TransitionOptions, Url} from './RouterContext'

import type {RouterProviderProps} from '../types'

/**
 * This router provider will be used as a temporary abstraction
 * to facilitate our migration to Next.js.
 * This should be deleted as we fully migrate the app to the framework.
 *
 * Use this provider in apps that use Next.js routing
 * @param props - The props for the router provider
 * @param props.children - The children component to render
 */
export const RouterProvider = ({children}: RouterProviderProps) => {
  const router = useNextRouter()

  // Parse the path with a mock origin, do not use any prop related to the origin
  const {pathname, search} = new URL(router.asPath, 'https://vio.com')

  const getQueryParams = () => parseQueryString(search)

  // Pages router methods are not stable across renders
  // https://github.com/vercel/next.js/issues/18127#issuecomment-1619721701
  const routerRef = useRef(router)

  const handlePush = useCallback(
    (url: Url, options: TransitionOptions = {}) =>
      routerRef.current.push(url, undefined, options),
    []
  )
  const handleReplace = useCallback(
    (url: Url, options: TransitionOptions = {}) =>
      routerRef.current.replace(url, undefined, options),
    []
  )

  return (
    <RouterContext.Provider
      value={{
        push: handlePush,
        replace: handleReplace,
        goBack: routerRef.current.back,
        getQueryParams,
        pathname,
        queryString: search
      }}
    >
      {children}
    </RouterContext.Provider>
  )
}
